import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import componentStyle from '../common/styles/Component.css'
import style from './Badge.css'

/**
 * Badges are used to inform users of the status of an object
 * or of an action that’s been taken. Commonly used in tabular
 * data to indicate status.
 *
 * @status ready
 * @category feedback
 * @slot - The badge content.
 * @slot icon - Used to place an icon at the start of badge.
 *
 * @cssprop [--n-badge-font-weight=var(--n-font-weight)] - Controls the font weight of the badge, using our [font tokens](/tokens/#font).
 * @cssprop [--n-badge-font-size=var(--n-font-size-s)] - Controls the font size of the badge, using our [font tokens](/tokens/#font).
 * @cssprop [--n-badge-padding-inline=var(--n-space-s)] - Controls the inline padding of the badge, using our [space tokens](/tokens/#space).
 * @cssprop [--n-badge-padding-block=0] - Controls the block padding of the badge, using our [space tokens](/tokens/#space). By default the badge height comes from its line height alone.
 * @cssprop [--n-badge-color] - Controls the text color of the badge. Overrides the variant default.
 * @cssprop [--n-badge-background-color] - Controls the background color of the badge. Overrides the variant default.
 * @cssprop [--n-badge-border-color] - Controls the border color of the badge. Overrides the variant default.
 */
@customElement('nord-badge')
export default class Badge extends LitElement {
  static styles = [componentStyle, style]

  /**
   * The type of badge.
   * Determines the background color of the badge.
   * @deprecated Use `variant` instead. If you previously used `"progress"`, use `"info"` instead.
   * @default "neutral"
   */
  @property({ reflect: true }) type?: 'warning' | 'success' | 'danger' | 'highlight' | 'info' | 'neutral' | 'progress'

  /**
   * The style variant of the badge.
   * Determines the background color of the badge.
   * @default "neutral"
   */
  @property({ reflect: true }) variant?: 'warning' | 'success' | 'danger' | 'highlight' | 'neutral' | 'info'

  /**
   * Strong badges are a secondary style for badges.
   * They provide more visual prominence and emphasize them.
   */
  @property({ reflect: true, type: Boolean }) strong = false

  render() {
    return html`
      <span class="n-badge">
        <slot name="icon"></slot>
        <slot></slot>
      </span>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-badge': Badge
  }
}
